/**
 * "Who was this sent to?" — the emails the app sent for an event's estimates,
 * deposit invoices and invoices (finance request 2026-09-16).
 *
 * Backend: `GET billingEmailDeliveries?eventId=&kind=` (`modules/invoices`),
 * read from `email_logs.metadata`. Only sends made after 2026-09-16 carry the
 * document link; older emails are not listed.
 */
import { http } from "./client";

export type BillingDeliveryKind = "estimate" | "depositInvoice" | "invoice";

export interface BillingDocumentStatus {
  kind: BillingDeliveryKind;
  documentId: string;
  documentNumber: string;
  configurationId: string | null;
  configurationName: string;
  savedAt: string | null;
  status: "saved" | "queued" | "sent" | "partial" | "failed" | "suppressed" | "test" | "unconfirmed";
  manualSend?: { revision: number; sentAt: string | null; recordedAt: string | null; recordedByName: string | null };
  sentAt: string | null;
  sentTo: string[];
  cc: string[];
}

export async function getBillingDocumentStatus(eventId: string, configurationId?: string): Promise<{ documents: BillingDocumentStatus[]; truncated: boolean }> {
  const { response, error } = await http({ method: "GET", url: "billingDocumentStatus",
    params: { eventId, ...(configurationId ? { configurationId } : {}) }, isToken: true });
  if (error || response?.status !== 200) throw new Error("Saved document status could not be checked.");
  const value = response?.data?.data;
  if (!Array.isArray(value?.documents)) throw new Error("Saved document status is unavailable.");
  return { documents: value.documents, truncated: value.truncated === true };
}

export interface BillingDelivery {
  _id: string;
  kind: BillingDeliveryKind;
  documentId: string | null;
  documentNumber: string | null;
  subject: string;
  to: string[];
  cc: string[];
  replyTo: string | null;
  status: "sent" | "failed" | "skipped" | string | null;
  deliveryStatus?: "sent" | "test" | "suppressed" | "failed" | "unconfirmed";
  error: string | null;
  sentAt: string | null;
  sentBy: string | null;
  customCopy: boolean;
  receipts?: Array<{ receiptId: string; chargeId: string; fileName: string; contentType: string; size: number }>;
}

export function getBillingEmailDeliveries(eventId: string, kind?: BillingDeliveryKind) {
  return http({
    method: "GET",
    url: "billingEmailDeliveries",
    params: { eventId, ...(kind ? { kind } : {}) },
    isToken: true,
  });
}

/** Administrative declaration only. Does not send email or change financial documents. */
export async function declareInvoiceSend(input: { eventId: string; invoiceId: string; expectedRevision: number; sent: boolean; sentAt?: string; note: string }) {
  const { response, error } = await http({ method: 'POST', url: 'invoiceManualSend', data: input, isToken: true });
  if (error || !response) throw new Error(error?.response?.data?.message || 'External send could not be recorded. Refresh before retrying.');
}
